import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { adminApi } from '../services/api';

// Shared RBAC + payment-action UI used by the Reconciliation dashboard and the
// Orders detail modal. An "item" is { orderId, paymentId, stripeStatus, amount,
// amountReceived, amountCapturable, email, bucket? }.

export const ROLE_RANK = { viewer: 0, support: 1, finance: 2, superadmin: 3 };
export const can = (role, min) => (ROLE_RANK[role] ?? -1) >= (ROLE_RANK[min] ?? 99);

export const ACTION_META = {
  capture: { label: 'Capture', cls: 'bg-green-600 hover:bg-green-700', needsAmount: false, confirm: false, role: 'support' },
  void: { label: 'Void', cls: 'bg-gray-500 hover:bg-gray-600', needsAmount: false, confirm: false, role: 'support' },
  refund: { label: 'Refund', cls: 'bg-red-600 hover:bg-red-700', needsAmount: true, needsReason: true, confirm: true, role: 'finance' },
  recharge: { label: 'Recharge', cls: 'bg-indigo-600 hover:bg-indigo-700', needsAmount: true, confirm: true, role: 'finance' },
};

export function actionsFor(item, role) {
  const st = item.stripeStatus;
  if (st === 'requires_capture') return ['capture', 'void'].filter((a) => can(role, ACTION_META[a].role));
  if (st === 'canceled') return ['recharge'].filter((a) => can(role, ACTION_META[a].role));
  if (st === 'succeeded') return ['refund'].filter((a) => can(role, ACTION_META[a].role));
  return [];
}

export const ActionButtons = ({ item, role, onAct, fullWidth }) => {
  const actions = actionsFor(item, role);
  if (!actions.length) return <span className="text-xs text-gray-300">—</span>;
  return (
    <div className={`flex flex-wrap gap-1.5 ${fullWidth ? '' : ''}`}>
      {actions.map((a) => (
        <button key={a} onClick={() => onAct(a, item)}
          className={`min-h-[40px] px-3 py-1.5 rounded text-xs font-semibold text-white ${ACTION_META[a].cls} ${fullWidth ? 'flex-1' : ''}`}>
          {ACTION_META[a].label}
        </button>
      ))}
    </div>
  );
};

export const PaymentActionModal = ({ action, item, onClose, onDone }) => {
  const meta = ACTION_META[action];
  const defaultAmount = action === 'recharge'
    ? ((item.amount || 0) / 100).toFixed(2)
    : ((item.amountReceived || item.amount || 0) / 100).toFixed(2);
  const [amount, setAmount] = useState(defaultAmount);
  const [reason, setReason] = useState('requested_by_customer');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  // Stable per-modal id so a double-submit dedupes, but a fresh modal-open is a new
  // authorized charge (used as the recharge idempotency key server-side).
  const [actionId] = useState(() =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const ready = !meta.confirm || typed.trim().toUpperCase() === 'CONFIRM';

  const submit = async () => {
    setBusy(true);
    try {
      let res;
      if (action === 'capture') res = await adminApi.captureOrder(item.orderId);
      else if (action === 'void') res = await adminApi.voidOrder(item.orderId);
      else if (action === 'refund') res = await adminApi.refundOrder(item.orderId, Number(amount), reason);
      else if (action === 'recharge') res = await adminApi.rechargeOrder(item.orderId, Number(amount), actionId);
      const d = res.data || {};
      if (d.success === false) toast.warn(`${meta.label}: ${d.status || d.message || 'not completed'}`);
      else {
        let note = '';
        if (action === 'recharge' && d.customerNotified === false) note = ' — remember to notify the customer';
        else if (action === 'refund') note = d.customerNotified ? ' · customer emailed' : ' · email not sent';
        toast.success(`${meta.label} done for ${item.orderId}${note}`);
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || `${meta.label} failed`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-gray-600 bg-opacity-75 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mt-10 sm:mt-20 p-5">
        <h3 className="text-lg font-bold text-gray-900">{meta.label} — {item.orderId}</h3>
        {item.email && <p className="text-sm text-gray-500 mt-0.5 break-all">{item.email}</p>}
        <p className="text-xs text-gray-400 mt-1">Stripe: {item.stripeStatus} · PI {item.paymentId}</p>

        {meta.needsAmount && (
          <label className="block mt-4 text-sm">
            <span className="text-gray-700">Amount (USD)</span>
            <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px]" />
          </label>
        )}
        {meta.needsReason && (
          <label className="block mt-3 text-sm">
            <span className="text-gray-700">Reason</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px]">
              <option value="requested_by_customer">Requested by customer</option>
              <option value="duplicate">Duplicate</option>
              <option value="fraudulent">Fraudulent</option>
            </select>
          </label>
        )}
        {meta.confirm && (
          <label className="block mt-3 text-sm">
            <span className="text-gray-700">Type <strong>CONFIRM</strong> to authorize this money action</span>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="CONFIRM"
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px] uppercase" />
          </label>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} disabled={busy}
            className="min-h-[44px] px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={!ready || busy}
            className={`min-h-[44px] px-4 py-2 rounded-md text-sm font-semibold text-white disabled:opacity-40 ${meta.cls}`}>
            {busy ? 'Working…' : meta.label}
          </button>
        </div>
      </div>
    </div>
  );
};
